import { Loader, generateFlinksIframeUrl, reportErrorToConsole, useCompleteApplicantFlinks, useGetCreditApplicationById } from '@applyonline/shared'
import { EApplicantType, EFinancingProgram, EFlinksEventSteps, EOriginSystemId } from '@if/core/types'
import { getEnvRuntimeVariable } from '@if/core/utils'
import { useEffect, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { applicantFlinksId } from '../../data/credit-application-selectors'

const FlinksIncomeValidationPage = (): JSX.Element => {
  const { id, applicantType } = useParams<{ id: string; applicantType: EApplicantType }>()
  const [searchParams] = useSearchParams()
  const institutionShortName = searchParams.get('institutionShortName') ?? undefined
  const { t, i18n } = useTranslation()
  const [completeFlinks, isCompletingFlinks] = useCompleteApplicantFlinks(EFinancingProgram.Personal)
  const [creditApp, isLoadingApp] = useGetCreditApplicationById(id!, EFinancingProgram.Personal, false)
  const [isInProgress, setIsInProgress] = useState(true)
  const externalServiceId = applicantFlinksId(creditApp, applicantType!)
  const navigate = useNavigate()
  const originSystemId = creditApp?.originSystemId

  useEffect(() => {
    window.addEventListener('message', (e: MessageEvent<{ step?: EFlinksEventSteps }>) => {
      if (e.data?.step) {
        // The request is done, we are receiving the answer
        if (e.data.step === EFlinksEventSteps.REDIRECT) {
          setIsInProgress(false)
          const completeFlinksDto = {
            id: id!,
            externalServiceId,
            applicantType: applicantType!,
          }

          completeFlinks(completeFlinksDto).catch(reportErrorToConsole)
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoadingApp || isCompletingFlinks)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  const iframeUrl = generateFlinksIframeUrl({
    baseUrl: import.meta.env.VITE_FLINKS_IFRAME,
    customerName: 'iFinance',
    isDemoMode: getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_DEMO_FLINKS),
    envTag: import.meta.env.VITE_ENV,
    externalServiceId,
    language: i18n.language,
    cleanWhitespace: true,
    institutionShortName
  })

  if (isInProgress)
    return <iframe title="flinksIFrame" width="100%" loading="eager" src={iframeUrl} style={{ flexGrow: 1 }} />

  return (
    <main id="flinks-validation" className="general-message">
      <h1 style={{ marginBottom: '1rem' }}>{t('common.thankYou')}</h1>
      <h3>{t('flinks.accountRetrieved')}</h3>
      <h3>{t('flinks.processingInfomation')}</h3>
      <div className="btn-group full-width-mobile" style={{ marginTop: '16px' }}>
        {originSystemId !== EOriginSystemId.MerchantDashboard && (
          <button
            type="button"
            className="btn btn-blue"
            style={{ gap: '2rem', minWidth: '0', justifyContent: 'center' }}
            onClick={() => navigate(`/creditapplication/${id}`)}
          >
            <i className="fa-regular fa-arrow-left" />
            <span>{t('flinks.back')}</span>
          </button>
        )}
      </div>
    </main>
  )
}

export default FlinksIncomeValidationPage
